/**
 * Peças de tela das Novidades (spec 036): o texto com os termos do tenant e o
 * negrito, o corpo (parágrafos e listas) e as imagens — uma, ou um carrossel
 * pequeno com setas e pontos, como os cartões do TiFlux.
 */
import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  renderNewsText,
  splitBold,
  type NewsBlock,
  type NewsImage,
  type NewsText as NewsTextValue,
  type NewsTextVars,
} from './news-model';

/** texto com `{termo}` resolvido e `**negrito**` */
export function NewsText({ text, vars }: { text: NewsTextValue; vars: NewsTextVars }) {
  const parts = splitBold(renderNewsText(text, vars));
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="font-semibold text-foreground">
            {part}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export function NewsBody({
  blocks,
  vars,
  className,
}: {
  blocks: readonly NewsBlock[];
  vars: NewsTextVars;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2 text-sm leading-relaxed text-muted-foreground', className)}>
      {blocks.map((b, i) =>
        b.kind === 'p' ? (
          <p key={i}>
            <NewsText text={b.text} vars={vars} />
          </p>
        ) : (
          <ul key={i} className="list-disc space-y-1 pl-5">
            {b.items.map((item, j) => (
              <li key={j}>
                <NewsText text={item} vars={vars} />
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}

/** uma imagem, ou o carrossel com setas e pontos quando há mais de uma */
export function NewsImages({ images, vars }: { images: readonly NewsImage[]; vars: NewsTextVars }) {
  const [index, setIndex] = useState(0);
  if (images.length === 0) return null;
  const current = images[Math.min(index, images.length - 1)]!;
  const many = images.length > 1;
  const go = (delta: number) => setIndex((i) => (i + delta + images.length) % images.length);
  return (
    <figure className="relative overflow-hidden rounded-md border bg-muted/40">
      <img
        src={current.src}
        alt={renderNewsText(current.alt, vars)}
        loading="lazy"
        className="block h-auto w-full"
      />
      {many ? (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Imagem anterior"
            className="absolute top-1/2 left-1.5 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-card/90 text-foreground shadow-sm transition-colors hover:bg-card"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Próxima imagem"
            className="absolute top-1/2 right-1.5 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-card/90 text-foreground shadow-sm transition-colors hover:bg-card"
          >
            <ChevronRight className="size-4" />
          </button>
          <div className="absolute inset-x-0 bottom-1.5 flex justify-center gap-1.5">
            {images.map((img, i) => (
              <button
                key={img.src}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Imagem ${i + 1} de ${images.length}`}
                aria-current={i === index}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === index ? 'w-4 bg-primary' : 'w-1.5 bg-foreground/30 hover:bg-foreground/50'
                )}
              />
            ))}
          </div>
        </>
      ) : null}
    </figure>
  );
}
